import { sectionRenderer } from '@theme/section-renderer';

const OWNED_PROPERTIES = [
  'Hemming requested',
  'Finished inseam',
  '_Hemming requested',
  '_Finished inseam',
  'Hemming inseam',
];
const PUBLIC_REQUEST = 'Hemming requested';
const PUBLIC_INSEAM = 'Finished inseam';
const PRIVATE_ID = '_Hemming selection id';
const MIN_HALF_INCHES = 40;
const MAX_HALF_INCHES = 66;

export function normalizeProductType(value) {
  return String(value || '').trim().toLowerCase();
}

export function normalizeInseam(value) {
  const match = String(value || '')
    .trim()
    .toLowerCase()
    .replace(/inches?|["″]/g, 'in')
    .match(/^(\d+(?:\.5)?)\s*(?:in)?$/);
  if (!match) return null;
  const halfInches = Number(match[1]) * 2;
  if (!Number.isInteger(halfInches) || halfInches < MIN_HALF_INCHES || halfInches > MAX_HALF_INCHES) {
    return null;
  }
  const inches = halfInches / 2;
  return `${Number.isInteger(inches) ? inches.toFixed(0) : inches.toFixed(1)} in`;
}

export function applyHemmingDelta(properties, requested, inseam, identity) {
  const next = { ...(properties || {}) };
  for (const key of OWNED_PROPERTIES) delete next[key];
  delete next[PRIVATE_ID];
  if (identity) next[PRIVATE_ID] = identity;
  if (requested) {
    const normalized = normalizeInseam(inseam);
    if (!normalized) throw new Error('Choose a finished inseam from 20 to 33 inches.');
    next[PUBLIC_REQUEST] = 'Yes';
    next[PUBLIC_INSEAM] = normalized;
  }
  return next;
}

export function isValidHemmedItem(item) {
  return (
    normalizeProductType(item?.product_type) === 'jeans' &&
    item?.properties?.[PUBLIC_REQUEST] === 'Yes' &&
    normalizeInseam(item?.properties?.[PUBLIC_INSEAM]) !== null
  );
}

export function shouldWarnForHemmingNote(note) {
  const value = String(note || '');
  const positive = /\b(?:hem(?:m?(?:ed|ing|e))?|hemm|inseam|in[-\s]?seam|shorten(?:ed|ing)?|finished\s+length|pant\s+length|leg\s+length)\b/i;
  const measurement = /(?:^|[^\d])(?:2\d|3\d)(?:\.5)?\s*(?:["″]|inches?\b)/i;
  const negative = /\b(?:do\s+not|don't|dont|no|without|not)\s+(?:hem|hemming|shorten|alter)/i;
  const returnContext = /\b(?:return|exchange|refund|returned|returning)\b/i;
  return !negative.test(value) && !returnContext.test(value) && (positive.test(value) || measurement.test(value));
}

class CartCoordinator {
  constructor() {
    this.root = window.Shopify?.routes?.root || '/';
    this.cart = null;
    this.chain = Promise.resolve();
    this.pending = 0;
    this.revision = 0;
    this.failed = false;
    this.blockedMessage = '';
    this.errorMessage = '';
    this.noteIntent = null;
    this.noteSequence = 0;
    this.noteTimer = null;
    this.discountErrorMessage = '';
    this.checkoutPreparing = false;
    this.refreshing = false;
    this.initialized = false;
  }

  initialize() {
    if (this.initialized) return;
    this.initialized = true;
    document.addEventListener('change', this.onChange, true);
    document.addEventListener('input', this.onInput, true);
    document.addEventListener('click', this.onClick, true);
    document.addEventListener('submit', this.onSubmit, true);
    document.addEventListener('quantity-selector:update', this.onQuantity, true);
    document.addEventListener('cart:update', this.onExternalCartUpdate);
    document.addEventListener('visibilitychange', this.onVisibilityChange);
    window.addEventListener('storage', this.onStorage);
    this.setState('pending', 'Loading cart…');
    this.enqueue('bootstrap', async () => {
      await this.refetch();
      await this.normalizeConfirmedCart();
      await this.refreshSections();
    });
  }

  enqueue(label, operation) {
    this.pending += 1;
    this.failed = false;
    this.errorMessage = '';
    this.setState('pending', label === 'bootstrap' ? 'Loading cart…' : 'Saving…');
    const job = this.chain
      .catch(() => undefined)
      .then(operation)
      .then(() => {
        this.failed = false;
      })
      .catch(async (error) => {
        this.failed = true;
        this.errorMessage = error instanceof Error ? error.message : 'Cart changes could not be saved.';
        try {
          await this.refetch();
          await this.refreshSections();
        } catch (_refreshError) {
        }
        throw error;
      })
      .finally(() => {
        this.pending = Math.max(0, this.pending - 1);
        if (this.failed) {
          this.setState('failed', this.errorMessage);
        } else if (this.blockedMessage) {
          this.setState('blocked', this.blockedMessage);
        } else if (this.pending === 0) {
          this.setState('saved', 'Cart saved.');
        }
      });
    this.chain = job.catch(() => undefined);
    return job;
  }

  async request(path, body) {
    const response = await fetch(`${this.root}${path}`, {
      method: body === undefined ? 'GET' : 'POST',
      headers: body === undefined ? { Accept: 'application/json' } : {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
    if (!response.ok) {
      let message = 'Cart changes could not be saved.';
      try {
        const payload = await response.json();
        message = payload.description || payload.message || message;
      } catch (_error) {
      }
      throw new Error(message);
    }
    return response.json();
  }

  async refetch() {
    this.cart = await this.request('cart.js');
    this.revision += 1;
    this.reconcileLegacyBlock();
    return this.cart;
  }

  resolveLine(identity, fallbackKey) {
    if (!this.cart) throw new Error('Cart is not ready.');
    if (identity) {
      const matches = this.cart.items.filter((item) => item.properties?.[PRIVATE_ID] === identity);
      if (matches.length === 1) return matches[0];
      if (matches.length > 1) throw new Error('Two cart items share the same customization identity. Reload and try again.');
    }
    const byKey = this.cart.items.find((item) => item.key === fallbackKey);
    if (!byKey) throw new Error('This cart item changed. Reload and try again.');
    return byKey;
  }

  createIdentity() {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();
    const bytes = new Uint8Array(16);
    window.crypto.getRandomValues(bytes);
    return [...bytes].map((value) => value.toString(16).padStart(2, '0')).join('');
  }

  async saveHemming(row, requested, inseam) {
    await this.refetch();
    const identity = row.dataset.selectionId || this.createIdentity();
    const item = this.resolveLine(row.dataset.selectionId, row.dataset.lineKey);
    if (requested && normalizeProductType(item.product_type) !== 'jeans') {
      throw new Error('Hemming is available only for jeans.');
    }
    const originalProperties = { ...(item.properties || {}) };
    const properties = applyHemmingDelta(originalProperties, requested, inseam, identity);
    if (requested && item.quantity > 1) {
      await this.splitCustomizedLine(item, properties);
    } else {
      this.cart = await this.request('cart/change.js', { id: item.key, properties });
    }
    const matches = this.cart.items.filter((candidate) => candidate.properties?.[PRIVATE_ID] === identity);
    if (matches.length !== 1) throw new Error('The customized cart item could not be identified after saving.');
    this.validateUnrelatedProperties(originalProperties, matches[0].properties);
    await this.saveDerivedAttributes();
    this.revision += 1;
    this.broadcastInvalidation();
    await this.refreshSections();
  }

  validateUnrelatedProperties(before, after) {
    const owned = new Set([...OWNED_PROPERTIES, PRIVATE_ID]);
    for (const [key, value] of Object.entries(before || {})) {
      if (owned.has(key)) continue;
      if (after?.[key] !== value) throw new Error(`The cart could not preserve the line-item property “${key}”.`);
    }
  }

  async splitCustomizedLine(item, firstProperties) {
    const quantity = item.quantity;
    this.cart = await this.request('cart/change.js', {
      id: item.key,
      quantity: 1,
      properties: firstProperties,
    });
    const additions = [];
    for (let index = 1; index < quantity; index += 1) {
      const identity = this.createIdentity();
      const properties = { ...firstProperties, [PRIVATE_ID]: identity };
      const addition = { id: item.variant_id, quantity: 1, properties };
      const sellingPlan = item.selling_plan_allocation?.selling_plan?.id;
      if (sellingPlan) addition.selling_plan = sellingPlan;
      additions.push(addition);
    }
    if (additions.length) {
      await this.request('cart/add.js', { items: additions });
      await this.refetch();
    }
  }

  async changeQuantity(row, quantity) {
    await this.refetch();
    const identity = row.dataset.selectionId || '';
    const item = this.resolveLine(identity, row.dataset.key);
    if (quantity > 1 && isValidHemmedItem(item)) {
      const additions = [];
      for (let index = item.quantity; index < quantity; index += 1) {
        const properties = { ...item.properties, [PRIVATE_ID]: this.createIdentity() };
        const addition = { id: item.variant_id, quantity: 1, properties };
        const sellingPlan = item.selling_plan_allocation?.selling_plan?.id;
        if (sellingPlan) addition.selling_plan = sellingPlan;
        additions.push(addition);
      }
      if (additions.length) await this.request('cart/add.js', { items: additions });
      if (quantity < item.quantity) {
        this.cart = await this.request('cart/change.js', { id: item.key, quantity });
      } else {
        await this.refetch();
      }
    } else {
      this.cart = await this.request('cart/change.js', { id: item.key, quantity });
    }
    await this.saveDerivedAttributes();
    this.revision += 1;
    this.broadcastInvalidation();
    await this.refreshSections();
  }

  async removeLine(row) {
    await this.refetch();
    const item = this.resolveLine(row.dataset.selectionId || '', row.dataset.key);
    this.cart = await this.request('cart/change.js', { id: item.key, quantity: 0 });
    await this.saveDerivedAttributes();
    this.revision += 1;
    this.broadcastInvalidation();
    await this.refreshSections();
  }

  deriveAttributes() {
    const selections = (this.cart?.items || []).filter(isValidHemmedItem).map((item) => {
      const variant = item.variant_title && item.variant_title !== 'Default Title' ? ` — ${item.variant_title}` : '';
      return `${item.product_title}${variant}: ${normalizeInseam(item.properties[PUBLIC_INSEAM])}`;
    });
    return {
      'Hemming requested': selections.length ? 'Yes' : '',
      'Finished inseam': selections.join('; '),
    };
  }

  async saveDerivedAttributes() {
    const attributes = this.deriveAttributes();
    this.cart = await this.request('cart/update.js', { attributes });
    this.reconcileLegacyBlock();
  }

  async saveNote(note, sequence) {
    this.cart = await this.request('cart/update.js', { note });
    if (sequence === this.noteSequence) this.noteIntent = null;
    this.revision += 1;
    this.broadcastInvalidation();
    await this.refreshSections();
  }

  async saveDiscount(value, addedCode = '') {
    this.cart = await this.request('cart/update.js', { discount: value });
    if (addedCode) {
      const result = this.cart.discount_codes?.find(
        (discount) => discount.code?.toLowerCase() === addedCode.toLowerCase(),
      );
      if (!result?.applicable) {
        this.discountErrorMessage = `Discount code “${addedCode}” could not be applied.`;
        throw new Error(this.discountErrorMessage);
      }
    }
    this.discountErrorMessage = '';
    this.revision += 1;
    this.broadcastInvalidation();
    await this.refreshSections();
  }

  async normalizeConfirmedCart() {
    if (!this.cart) return;
    for (const snapshot of [...this.cart.items]) {
      const hasLegacyPublicInseam = snapshot.properties?.['Hemming inseam'] !== undefined;
      const requested = String(snapshot.properties?.[PUBLIC_REQUEST] || snapshot.properties?.['_Hemming requested'] || '').toLowerCase() === 'yes' || hasLegacyPublicInseam;
      const legacyInseam = snapshot.properties?.[PUBLIC_INSEAM] || snapshot.properties?.['Hemming inseam'] || snapshot.properties?.['_Finished inseam'];
      const valid = requested && normalizeProductType(snapshot.product_type) === 'jeans' && normalizeInseam(legacyInseam);
      const hasLegacy = snapshot.properties?.['_Hemming requested'] !== undefined || snapshot.properties?.['_Finished inseam'] !== undefined;
      const hasOwned = OWNED_PROPERTIES.some((property) => snapshot.properties?.[property] !== undefined);
      const needsIdentity = Boolean(valid) && !snapshot.properties?.[PRIVATE_ID];
      const needsCanonicalValue = Boolean(valid) && (
        snapshot.properties?.[PUBLIC_REQUEST] !== 'Yes' ||
        snapshot.properties?.[PUBLIC_INSEAM] !== normalizeInseam(legacyInseam)
      );
      if (valid && snapshot.quantity > 1) {
        const identity = snapshot.properties?.[PRIVATE_ID] || this.createIdentity();
        const properties = applyHemmingDelta(snapshot.properties, true, legacyInseam, identity);
        await this.splitCustomizedLine(snapshot, properties);
        continue;
      }
      if (!hasLegacy && !hasOwned && !needsIdentity && !needsCanonicalValue) continue;
      if (valid && !hasLegacy && !needsIdentity && !needsCanonicalValue) continue;
      const identity = valid
        ? snapshot.properties?.[PRIVATE_ID] || this.createIdentity()
        : snapshot.properties?.[PRIVATE_ID] || '';
      const properties = applyHemmingDelta(snapshot.properties, Boolean(valid), legacyInseam, identity);
      this.cart = await this.request('cart/change.js', { id: snapshot.key, properties });
    }
    this.reconcileLegacyBlock();
    if (!this.blockedMessage) await this.saveDerivedAttributes();
  }

  reconcileLegacyBlock() {
    const hasValidLines = (this.cart?.items || []).some(isValidHemmedItem);
    const legacyRequested = String(this.cart?.attributes?.['Hemming requested'] || '').toLowerCase() === 'yes';
    this.blockedMessage = legacyRequested && !hasValidLines
      ? 'Choose item-specific hemming or clear the legacy hemming request before checkout.'
      : '';
  }

  async clearLegacyAttributes() {
    await this.refetch();
    this.cart = await this.request('cart/update.js', {
      attributes: { 'Hemming requested': '', 'Finished inseam': '' },
    });
    this.blockedMessage = '';
    this.revision += 1;
    await this.refreshSections();
  }

  collectSurfaceState() {
    return {
      note: this.noteIntent ?? document.querySelector('[data-cart-note]')?.value ?? null,
      open: [...document.querySelectorAll('.cart-actions details[open]')]
        .map((details) => details.dataset.cartDisclosure)
        .filter(Boolean),
      activeId: document.activeElement?.id || '',
    };
  }

  restoreSurfaceState(state) {
    if (state.note !== null) {
      document.querySelectorAll('#cart-note').forEach((textarea) => {
        textarea.value = state.note;
      });
    }
    for (const disclosure of state.open) {
      document.querySelectorAll(`.cart-actions details[data-cart-disclosure="${CSS.escape(disclosure)}"]`)
        .forEach((details) => details.setAttribute('open', ''));
    }
    if (state.activeId) document.getElementById(state.activeId)?.focus({ preventScroll: true });
    this.updateNoteWarning();
    this.renderDiscountError();
    const stateName = this.failed ? 'failed' : this.blockedMessage ? 'blocked' : this.pending ? 'pending' : 'saved';
    this.setState(stateName, this.errorMessage || this.blockedMessage || 'Cart saved.');
  }

  async refreshSections() {
    if (this.refreshing) return;
    this.refreshing = true;
    const state = this.collectSurfaceState();
    try {
      const sectionIds = new Set(
        [...document.querySelectorAll('cart-items-component[data-section-id]')]
          .map((component) => component.dataset.sectionId)
          .filter(Boolean),
      );
      for (const sectionId of sectionIds) {
        await sectionRenderer.renderSection(sectionId, { cache: false });
      }
    } finally {
      this.refreshing = false;
      this.restoreSurfaceState(state);
    }
  }

  invalidVisibleSelection() {
    return [...document.querySelectorAll('[data-alteration-row]')].some((row) => {
      const checked = row.querySelector('[data-hemming-toggle]')?.checked;
      const value = row.querySelector('[data-inseam-input]')?.value;
      return checked && !normalizeInseam(value);
    });
  }

  validateCart() {
    if (!this.cart) throw new Error('Cart is not ready.');
    if (this.blockedMessage) throw new Error(this.blockedMessage);
    if (this.invalidVisibleSelection()) throw new Error('Choose a valid finished inseam for every hemmed item.');
    for (const item of this.cart.items) {
      const requested = item.properties?.[PUBLIC_REQUEST] === 'Yes';
      if (!requested) continue;
      if (!isValidHemmedItem(item)) throw new Error('A hemmed item is no longer eligible or has an invalid inseam.');
      if (!item.properties?.[PRIVATE_ID]) throw new Error('A hemmed item is missing its customization identity.');
    }
    const identities = this.cart.items.map((item) => item.properties?.[PRIVATE_ID]).filter(Boolean);
    if (new Set(identities).size !== identities.length) throw new Error('Customized cart items are not uniquely identified.');
  }

  async flushNote() {
    if (this.noteTimer) {
      window.clearTimeout(this.noteTimer);
      this.noteTimer = null;
    }
    if (this.noteIntent === null) return;
    const note = this.noteIntent;
    const sequence = this.noteSequence;
    await this.enqueue('Saving note…', () => this.saveNote(note, sequence));
  }

  async prepareCheckout(destination) {
    if (this.checkoutPreparing) return;
    this.checkoutPreparing = true;
    this.setState('pending', 'Preparing checkout…');
    try {
      await this.flushNote();
      await this.enqueue('Preparing checkout…', async () => {
        await this.refetch();
        await this.saveDerivedAttributes();
        await this.refetch();
        this.validateCart();
      });
      this.checkoutPreparing = false;
      this.setState('saved', 'Cart verified.');
      window.location.assign(destination);
    } catch (error) {
      this.checkoutPreparing = false;
      this.failed = true;
      this.errorMessage = error instanceof Error ? error.message : 'Checkout could not be prepared.';
      this.setState('blocked', this.errorMessage);
    }
  }

  enableAcceleratedCheckout() {
    document.querySelectorAll('[data-accelerated-checkout-host]').forEach((host) => {
      host.removeAttribute('inert');
      host.setAttribute('aria-disabled', 'false');
    });
  }

  lockAcceleratedCheckout() {
    document.querySelectorAll('[data-accelerated-checkout-host]').forEach((host) => {
      host.setAttribute('inert', '');
      host.setAttribute('aria-disabled', 'true');
    });
  }

  setState(state, message) {
    const blocked = this.checkoutPreparing || this.pending > 0 || this.noteIntent !== null || Boolean(this.noteTimer) || this.failed || Boolean(this.blockedMessage) || this.invalidVisibleSelection();
    document.querySelectorAll('[data-cart-save-state]').forEach((element) => {
      element.dataset.cartSaveState = state;
    });
    document.querySelectorAll('[data-alterations-status], [data-cart-coordinator-status]').forEach((element) => {
      element.textContent = message || '';
      element.dataset.cartState = state;
    });
    document.querySelectorAll('[data-safe-checkout]').forEach((button) => {
      button.disabled = blocked;
      button.setAttribute('aria-busy', this.pending > 0 ? 'true' : 'false');
    });
    document.querySelectorAll('.cart-items__wrapper, .cart-actions').forEach((surface) => {
      if (this.checkoutPreparing) {
        surface.setAttribute('inert', '');
        surface.dataset.coordinatorInert = 'true';
      } else if (surface.dataset.coordinatorInert === 'true') {
        surface.removeAttribute('inert');
        delete surface.dataset.coordinatorInert;
      }
    });
    if (blocked) {
      this.lockAcceleratedCheckout();
      return;
    }
    try {
      this.validateCart();
      this.enableAcceleratedCheckout();
    } catch (_error) {
      this.lockAcceleratedCheckout();
    }
  }

  renderDiscountError() {
    document.querySelectorAll('.cart-discount__error').forEach((element) => {
      element.classList.toggle('hidden', !this.discountErrorMessage);
      const text = element.querySelector('[ref="cartDiscountErrorDiscountCode"]');
      text?.classList.toggle('hidden', !this.discountErrorMessage);
      if (text && this.discountErrorMessage) text.textContent = this.discountErrorMessage;
    });
    document.querySelectorAll('.cart-discount__input').forEach((input) => {
      input.setAttribute('aria-invalid', this.discountErrorMessage ? 'true' : 'false');
    });
  }

  updateNoteWarning() {
    const note = this.noteIntent ?? document.querySelector('[data-cart-note]')?.value ?? '';
    const warning = shouldWarnForHemmingNote(note);
    document.querySelectorAll('[data-hemming-note-warning]').forEach((element) => {
      element.hidden = !warning;
    });
    document.querySelectorAll('[data-cart-note]').forEach((textarea) => {
      const warningElement = textarea.closest('cart-note')?.querySelector('[data-hemming-note-warning]');
      if (warning && warningElement?.id) {
        textarea.setAttribute('aria-invalid', 'true');
        textarea.setAttribute('aria-describedby', warningElement.id);
      } else {
        textarea.removeAttribute('aria-invalid');
        textarea.removeAttribute('aria-describedby');
      }
    });
  }

  onChange = (event) => {
    const toggle = event.target.closest?.('[data-hemming-toggle]');
    const select = event.target.closest?.('[data-inseam-input]');
    if (!toggle && !select) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    const row = (toggle || select).closest('[data-alteration-row]');
    if (!row) return;
    const rowToggle = row.querySelector('[data-hemming-toggle]');
    const rowSelect = row.querySelector('[data-inseam-input]');
    const wrapper = row.querySelector('[data-inseam-wrapper]');
    const requested = Boolean(rowToggle?.checked);
    if (wrapper) wrapper.hidden = !requested;
    if (rowSelect) rowSelect.required = requested;
    if (!requested && rowSelect) rowSelect.value = '';
    if (requested && !normalizeInseam(rowSelect?.value)) {
      this.setState('blocked', 'Choose a finished inseam.');
      rowSelect?.focus();
      return;
    }
    if (requested && !row.dataset.selectionId) row.dataset.selectionId = this.createIdentity();
    this.enqueue('Saving hemming…', () => this.saveHemming(row, requested, rowSelect?.value || ''));
  };

  onInput = (event) => {
    if (event.target.matches?.('.cart-discount__input')) {
      this.discountErrorMessage = '';
      this.renderDiscountError();
      return;
    }
    if (!event.target.matches?.('[data-cart-note]')) return;
    event.stopImmediatePropagation();
    this.noteIntent = event.target.value;
    this.noteSequence += 1;
    this.updateNoteWarning();
    this.setState('pending', 'Saving note…');
    window.clearTimeout(this.noteTimer);
    this.noteTimer = window.setTimeout(() => {
      this.noteTimer = null;
      const note = this.noteIntent;
      const sequence = this.noteSequence;
      this.enqueue('Saving note…', () => this.saveNote(note, sequence));
    }, 250);
  };

  onQuantity = (event) => {
    const row = event.target.closest?.('.cart-items__table-row[data-key]');
    if (!row) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    this.enqueue('Updating quantity…', () => this.changeQuantity(row, Number(event.detail.quantity)));
  };

  onClick = (event) => {
    const remove = event.target.closest?.('.cart-items__remove');
    if (remove) {
      const row = remove.closest('.cart-items__table-row[data-key]');
      if (!row) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.enqueue('Removing item…', () => this.removeLine(row));
      return;
    }
    const clearLegacy = event.target.closest?.('[data-clear-legacy-hemming]');
    if (clearLegacy) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.enqueue('Clearing legacy hemming…', () => this.clearLegacyAttributes());
      return;
    }
    const shortcut = event.target.closest?.('[data-open-alterations]');
    if (shortcut) {
      event.preventDefault();
      const cartSurface = shortcut.closest('cart-items-component');
      const alterations = cartSurface?.querySelector('cart-alterations details');
      alterations?.setAttribute('open', '');
      cartSurface?.querySelector('[data-hemming-toggle]')?.focus();
      return;
    }
    const checkout = event.target.closest?.('[data-safe-checkout]');
    if (checkout) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.prepareCheckout(checkout.dataset.checkoutUrl || `${this.root}checkout`);
      return;
    }
    const removeDiscount = event.target.closest?.('.cart-discount__pill-remove');
    if (removeDiscount) {
      event.preventDefault();
      event.stopImmediatePropagation();
      const code = removeDiscount.closest('[data-discount-code]')?.dataset.discountCode;
      if (!code) return;
      const existing = [...new Set(
        [...document.querySelectorAll('[data-discount-code]')]
          .map((pill) => pill.dataset.discountCode)
          .filter((value) => value && value !== code),
      )];
      this.enqueue('Updating discount…', () => this.saveDiscount(existing.join(',')));
    }
  };

  onSubmit = (event) => {
    if (event.target.matches?.('#cart-form')) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.setState('blocked', 'Use the checkout button after cart changes finish saving.');
      return;
    }
    if (!event.target.matches?.('.cart-discount__form')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    const input = event.target.querySelector('input[name="discount"]');
    const value = input?.value.trim();
    if (!value) return;
    const existing = [...new Set(
      [...document.querySelectorAll('[data-discount-code]')]
        .map((pill) => pill.dataset.discountCode)
        .filter(Boolean),
    )];
    if (!existing.some((code) => code.toLowerCase() === value.toLowerCase())) existing.push(value);
    this.enqueue('Applying discount…', () => this.saveDiscount(existing.join(','), value));
  };

  onExternalCartUpdate = (event) => {
    if (event.detail?.data?.source === 'ub-cart-coordinator') return;
    this.enqueue('Refreshing cart…', async () => {
      await this.refetch();
      await this.normalizeConfirmedCart();
      await this.refreshSections();
    });
  };

  onVisibilityChange = () => {
    if (document.visibilityState !== 'visible') return;
    this.enqueue('Refreshing cart…', async () => {
      await this.refetch();
      await this.normalizeConfirmedCart();
      await this.refreshSections();
    });
  };

  onStorage = (event) => {
    if (event.key !== 'ub-cart-revision') return;
    this.enqueue('Refreshing cart…', async () => {
      await this.refetch();
      await this.normalizeConfirmedCart();
      await this.refreshSections();
    });
  };

  broadcastInvalidation() {
    try {
      window.localStorage.setItem('ub-cart-revision', `${Date.now()}`);
    } catch (_error) {
    }
  }
}

export const cartCoordinator = new CartCoordinator();

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => cartCoordinator.initialize(), { once: true });
} else {
  cartCoordinator.initialize();
}
